import { useCallback, useEffect, useState } from 'react';
import type { Operation } from '@vigilo/shared';
import { get, post } from '@vigilo/api-client';
import { useMockMode } from '@/shell/MockModeContext';
import { MOCK_OPERATIONS } from '../data/mock';

export const CROWD_OPERATIONS_CHANGED_EVENT = 'vigilo:crowd:operations-changed';

export interface OperationZoneTeamAssignmentDto {
  zoneId: string;
  teamId: string | null;
  inheritedFromDefault: boolean;
}

export interface CreateOperationDto {
  name:            string;
  venueId:         string;
  eventType:       string;
  scheduledStart:  string;  // ISO 8601
  scheduledEnd:    string;  // ISO 8601
  assignedZoneIds: string[];
  temporaryZoneIds?: string[];
  assignedTeams?: string[];
  defaultTeamId?: string | null;
  zoneTeamAssignments?: OperationZoneTeamAssignmentDto[];
}

export interface CloseSummary {
  openAlertCount:      number;
  escalatedAlertCount: number;
  temporaryZoneCount:  number;
  permanentZoneCount:  number;
}

export interface OperationsHookState {
  operations:        Operation[];
  loading:           boolean;
  error:             string | null;
  createOperation:   (dto: CreateOperationDto) => Promise<Operation>;
  activateOperation: (id: string) => Promise<Operation>;
  pauseOperation:    (id: string) => Promise<Operation>;
  resumeOperation:   (id: string) => Promise<Operation>;
  closeOperation:    (id: string, force: boolean) => Promise<Operation>;
  refreshOperations: () => Promise<void>;
}

function unwrap<T>(body: T | { data?: T }): T {
  const b = body as { data?: T };
  return (b.data !== undefined ? b.data : body) as T;
}

function unwrapList<T>(body: T[] | { data?: T[] }): T[] {
  return Array.isArray(body) ? body : (body.data ?? []);
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function notifyOperationsChanged(): void {
  window.dispatchEvent(new Event(CROWD_OPERATIONS_CHANGED_EVENT));
}

function validateCreateOperationDto(dto: CreateOperationDto): void {
  if (!UUID_RE.test(dto.venueId)) {
    throw new Error('Select a valid venue before creating an operation.');
  }
  if (dto.assignedZoneIds.some(id => !UUID_RE.test(id))) {
    throw new Error('Select only valid zones before creating an operation.');
  }
  if ((dto.temporaryZoneIds ?? []).some(id => !UUID_RE.test(id))) {
    throw new Error('Select only valid temporary zones before creating an operation.');
  }
  if ((dto.temporaryZoneIds ?? []).some(id => !dto.assignedZoneIds.includes(id))) {
    throw new Error('Temporary zones must be selected for this operation.');
  }
  if (dto.defaultTeamId && !UUID_RE.test(dto.defaultTeamId)) {
    throw new Error('Select a valid default team before creating an operation.');
  }
  if ((dto.zoneTeamAssignments ?? []).some(assignment => !UUID_RE.test(assignment.zoneId))) {
    throw new Error('Select only valid zones before assigning teams.');
  }
  if ((dto.zoneTeamAssignments ?? []).some(assignment => assignment.teamId !== null && assignment.teamId !== undefined && !UUID_RE.test(assignment.teamId))) {
    throw new Error('Select only valid teams before creating an operation.');
  }
  if ((dto.zoneTeamAssignments ?? []).some(assignment => !dto.assignedZoneIds.includes(assignment.zoneId))) {
    throw new Error('Zone team assignments must match selected operation zones.');
  }
}

export function useOperations(): OperationsHookState {
  const { isMock } = useMockMode();
  const [operations, setOperations] = useState<Operation[]>(() =>
    isMock ? [...MOCK_OPERATIONS] : []
  );
  const [loading, setLoading] = useState(!isMock);
  const [error,   setError]   = useState<string | null>(null);

  const refreshOperations = useCallback(async (): Promise<void> => {
    if (isMock) return;
    setLoading(true);
    setError(null);
    try {
      const body = await get<Operation[] | { data?: Operation[] }>('/operations');
      setOperations(unwrapList(body));
    } catch {
      setError('Failed to load operations — check Core API');
    } finally {
      setLoading(false);
    }
  }, [isMock]);

  useEffect(() => {
    if (isMock) { setLoading(false); return; }
    refreshOperations();
  }, [isMock, refreshOperations]);

  useEffect(() => {
    if (isMock) return;
    function handleOperationsChanged() {
      void refreshOperations();
    }
    window.addEventListener(CROWD_OPERATIONS_CHANGED_EVENT, handleOperationsChanged);
    return () => window.removeEventListener(CROWD_OPERATIONS_CHANGED_EVENT, handleOperationsChanged);
  }, [isMock, refreshOperations]);

  const createOperation = useCallback(async (dto: CreateOperationDto): Promise<Operation> => {
    validateCreateOperationDto(dto);

    if (isMock) {
      await new Promise(r => setTimeout(r, 800));
      const now = new Date().toISOString();
      const op: Operation = {
        id:              crypto.randomUUID(),
        name:            dto.name,
        venueId:         dto.venueId,
        eventType:       dto.eventType,
        status:          'scheduled',
        scheduledStart:  dto.scheduledStart,
        scheduledEnd:    dto.scheduledEnd,
        actualStart:     null,
        actualEnd:       null,
        assignedZoneIds: dto.assignedZoneIds,
        assignedTeams:   dto.assignedTeams ?? [],
        defaultTeamId:   dto.defaultTeamId ?? null,
        zoneTeamAssignments: dto.zoneTeamAssignments ?? [],
        createdBy:       'mock-user-001',
        closedBy:        null,
        createdAt:       now,
        updatedAt:       now,
      };
      setOperations(prev => [op, ...prev]);
      notifyOperationsChanged();
      return op;
    }
    const body = await post<Operation | { data?: Operation }>('/operations', dto);
    const op = unwrap(body);
    setOperations(prev => [op, ...prev]);
    notifyOperationsChanged();
    return op;
  }, [isMock]);

  const activateOperation = useCallback(async (id: string): Promise<Operation> => {
    if (isMock) {
      await new Promise(r => setTimeout(r, 200));
      const now = new Date().toISOString();
      let result: Operation | null = null;
      setOperations(prev => prev.map(op => {
        if (op.id === id) {
          result = { ...op, status: 'active' as const, actualStart: now, updatedAt: now };
          return result;
        }
        return op;
      }));
      if (!result) throw new Error(`Operation ${id} not found`);
      return result;
    }
    const body = await post<Operation | { data?: Operation }>(`/operations/${id}/activate`, {});
    const op = unwrap(body);
    setOperations(prev => prev.map(p => p.id === op.id ? op : p));
    notifyOperationsChanged();
    return op;
  }, [isMock]);

  const pauseOperation = useCallback(async (id: string): Promise<Operation> => {
    if (isMock) {
      await new Promise(r => setTimeout(r, 200));
      const now = new Date().toISOString();
      let result: Operation | null = null;
      setOperations(prev => prev.map(op => {
        if (op.id === id) {
          result = { ...op, status: 'paused' as const, updatedAt: now };
          return result;
        }
        return op;
      }));
      if (!result) throw new Error(`Operation ${id} not found`);
      return result;
    }
    const body = await post<Operation | { data?: Operation }>(`/operations/${id}/pause`, {});
    const op = unwrap(body);
    setOperations(prev => prev.map(p => p.id === op.id ? op : p));
    notifyOperationsChanged();
    return op;
  }, [isMock]);

  const resumeOperation = useCallback(async (id: string): Promise<Operation> => {
    if (isMock) {
      await new Promise(r => setTimeout(r, 200));
      const now = new Date().toISOString();
      let result: Operation | null = null;
      setOperations(prev => prev.map(op => {
        if (op.id === id) {
          result = { ...op, status: 'active' as const, updatedAt: now };
          return result;
        }
        return op;
      }));
      if (!result) throw new Error(`Operation ${id} not found`);
      return result;
    }
    const body = await post<Operation | { data?: Operation }>(`/operations/${id}/resume`, {});
    const op = unwrap(body);
    setOperations(prev => prev.map(p => p.id === op.id ? op : p));
    notifyOperationsChanged();
    return op;
  }, [isMock]);

  const closeOperation = useCallback(async (id: string, force: boolean): Promise<Operation> => {
    if (isMock) {
      await new Promise(r => setTimeout(r, 800));
      const now = new Date().toISOString();
      let result: Operation | null = null;
      setOperations(prev => prev.map(op => {
        if (op.id === id) {
          result = { ...op, status: 'closed' as const, actualEnd: now, updatedAt: now };
          return result;
        }
        return op;
      }));
      if (!result) throw new Error(`Operation ${id} not found`);
      return result;
    }
    const body = await post<Operation | { data?: Operation }>(`/operations/${id}/close`, { force });
    const op = unwrap(body);
    setOperations(prev => prev.map(p => p.id === op.id ? op : p));
    notifyOperationsChanged();
    return op;
  }, [isMock]);

  return {
    operations,
    loading,
    error,
    createOperation,
    activateOperation,
    pauseOperation,
    resumeOperation,
    closeOperation,
    refreshOperations,
  };
}
